import { AdmissionGroup } from '@domain/admission-group';
import { EventSection } from '@domain/event-section';
import { createEventGroupsKey } from '@hooks/events/use-get-admission-groups';
import { createEventSectionsKey } from '@hooks/events/use-get-event-sections';
import { QueryClient } from '@tanstack/react-query';

import { MoveOfferRequest } from '../../hooks/admissions/use-move-offer';

import { moveOfferItemToPosition } from './utils/move-offer-item-to-position';
import { normalizePositions, normalizeSectionPositions } from './utils/normalize-positions';
import { removeOfferItemFromPosition } from './utils/remove-offer-item-from-position';

export const updateOfferPosition = (
  queryClient: QueryClient,
  eventId: string,
  offerId: string,
  body: MoveOfferRequest
) => {
  const { from, to, toPosition } = body;

  // Get current sections and groups data
  const sectionsQueryKey = createEventSectionsKey(eventId);
  const groupsQueryKey = createEventGroupsKey(eventId);

  const sections = queryClient.getQueryData<EventSection[]>(sectionsQueryKey);
  const groups = queryClient.getQueryData<AdmissionGroup[]>(groupsQueryKey);

  if (!sections) return;

  // Shallow clone with targeted deep cloning only for affected items
  // This is much faster than structuredClone for large objects
  const updatedSections = sections.map((section) => {
    // Only deep clone the section that will be modified
    if (section.id === from.id || section.id === to.id) {
      return {
        ...section,
        items: [...section.items],
      };
    }
    return section;
  });

  const updatedGroups = groups
    ? groups.map((group) => {
        // Only deep clone the group that will be modified
        if (group.id === from.id || group.id === to.id) {
          return {
            ...group,
            items: [...group.items],
          };
        }
        return group;
      })
    : [];

  // Remove item from source
  const itemToMove = removeOfferItemFromPosition(from, offerId, updatedSections, updatedGroups);

  if (!itemToMove) return;

  // Add item to destination (subtract 1 from toPosition as it was sent to BE with +1)
  const adjustedPosition = toPosition - 1;
  moveOfferItemToPosition(to, adjustedPosition, itemToMove, updatedSections, updatedGroups);

  // Normalize positions to be incremental (1...N)
  normalizeSectionPositions(updatedSections, from, to);
  if (groups) {
    normalizePositions(updatedGroups, from, to);
  }

  // Update the query cache
  queryClient.setQueryData(sectionsQueryKey, updatedSections);
  if (groups) {
    queryClient.setQueryData(groupsQueryKey, updatedGroups);
  }
};
